import { useEffect, useState } from "react";
import { LuHotel } from "react-icons/lu";
import Button from "../components/common/Button";
import { useNavigate } from "react-router-dom";
import { ROUTE_PATH, STORAGE_KEY } from "../constants";
import { getRoomList } from "../api";
import Spinner from "../components/common/Spinner";
import Notification from "../components/Notification";
import { AxiosError } from "axios";
import { RoomInfoResponse } from "../types/room";
import { useTranslation } from "react-i18next";

const RoomPage = () => {
  const [roomList, setRoomList] = useState<RoomInfoResponse[]>([]);
  const [roomImage, setRoomImage] = useState("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedRoom, setSelectedRoom] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const { t } = useTranslation();

  const filteredRooms = roomList.filter(
    (room) => room.room_no.includes(searchQuery) || room.room_type_desc.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleSelectRoom = (room_no: string) => {
    setSelectedRoom(room_no);
  };

  const handleNavigation = () => {
    localStorage.setItem(STORAGE_KEY.ROOM_NO, selectedRoom);
    navigate(ROUTE_PATH.PAYMENT);
  };

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { room_list, rooms_image },
        } = await getRoomList();
        setRoomList(room_list ?? []);
        setRoomImage(rooms_image);
      } catch (error) {
        if (error instanceof AxiosError) {
          setErrorMessage(error.response?.data.message);
          setIsError(true);
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  if (isLoading) return <Spinner />;
  if (isError) return <Notification message={errorMessage} />;

  return (
    <div className="relative space-y-6">
      <h1 className="text-xl font-bold">{t("room_select")}</h1>
      <img src={roomImage} alt="room-image" className="w-full max-h-60 object-cover rounded-[10px]" />
      <div className="space-y-5">
        <input
          type="text"
          placeholder={t("room_search")}
          className="w-full p-3 text-sm border border-gray-300 rounded-md"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="flex flex-col gap-2.5">
          {filteredRooms.map((room) => (
            <div
              key={room.room_no}
              className={`px-4 py-2.5 rounded-lg shadow-sm border-2 ${
                selectedRoom === room.room_no ? "border-purple-500 bg-purple-50" : "border-gray-200"
              }`}
              onClick={() => handleSelectRoom(room.room_no)}
            >
              <p className="text-sm mb-2">{room.floor_name}</p>
              <div className="flex items-center gap-x-2">
                <LuHotel size={20} />
                <span className=" font-semibold text-gray-800">{room.room_no}</span>
              </div>
              <span className="text-sm text-gray-600">{room.room_type_desc}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="sticky bottom-0 space-y-3 py-3 bg-white border-t">
        <p>
          {t("selected_room")}
          <span className="pl-1 text-lg text-purple-700 font-bold">{selectedRoom}</span>
        </p>
        <Button onClick={handleNavigation} disabled={!selectedRoom}>
          {t("select")}
        </Button>
      </div>
    </div>
  );
};

export default RoomPage;
